import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Button, Alert, Chip, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import creditService from "../../services/creditService";
import { db } from "../../firebase/config";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { logAdminAction } from "../../services/adminService";
import { validateCreditAssign } from "../../utils/validation";

export default function AdminCreditPanel({ user }) {
  const [credits, setCredits] = useState(0);
  const [amount, setAmount] = useState(100);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [assignByEmail, setAssignByEmail] = useState(false);
  const [email, setEmail] = useState("");

  const refresh = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const ref = doc(db, "users", user.id);
      const snap = await getDoc(ref);
      setCredits(snap.exists() ? (snap.data().totalCredits || 0) : 0);
      const h = await creditService.getUserCredits(user.id, { limit: 25 });
      setHistory(h);
    } catch (e) {
      setError(e.message || "Failed to load credits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      await refresh();
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleAssign = async () => {
    setError("");
    setSuccess("");
    try {
      const v = validateCreditAssign({ amount, assignByEmail, email });
      if (!v.valid) {
        setError(v.errors.join("; "));
        return;
      }
      let targetUserId = user?.id;
      if (assignByEmail) {
        if (!email) {
          setError("Enter an email address");
          return;
        }
        const q = query(collection(db, "users"), where("email", "==", email.toLowerCase()));
        const snap = await getDocs(q);
        if (snap.empty) {
          setError("No user found with that email");
          return;
        }
        targetUserId = snap.docs[0].id;
      }
      await creditService.assignCredit({
        assignedTo: targetUserId,
        amount: Number(amount),
        activityType: "admin_assigned",
        description: "Admin credit allocation",
        assignedBy: "ADMIN"
      });
      await logAdminAction("assign_credit", "ADMIN", targetUserId, { amount, email: assignByEmail ? email : undefined });
      await refresh();
      setSuccess("Credits assigned successfully");
    } catch (e) {
      setError(e.message || "Failed to assign credits");
    }
  };

  return (
    <Card sx={{ borderTop: "6px solid #003366" }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>Credit Management</Typography>
        {!user?.id ? (
          <Typography color="text.secondary">Select a user in Onboarding panel to manage credits.</Typography>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <Chip label={`Balance: ${credits}`} color="primary" />
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(parseInt(e.target.value || 0))}
                  size="small"
                  inputProps={{ "aria-label": "credit amount" }}
                />
                <Button variant="contained" onClick={handleAssign} sx={{ backgroundColor: "#C1272D", "&:hover": { backgroundColor: "#A51F25" } }}>
                  Add Credit
                </Button>
              </Box>
              <FormControlLabel
                control={<Switch checked={assignByEmail} onChange={(e) => setAssignByEmail(e.target.checked)} />}
                label="Assign by Email"
              />
              {assignByEmail && (
                <TextField
                  label="User Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  placeholder="user@example.com"
                  inputProps={{ "aria-label": "user email" }}
                />
              )}
              <Button variant="outlined" onClick={refresh}>Refresh</Button>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent Transactions</Typography>
            {loading ? (
              <CircularProgress />
            ) : history.length === 0 ? (
              <Typography color="text.secondary">No transactions</Typography>
            ) : (
              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                {history.map(h => (
                  <Box key={h.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5, mb: 1 }}>
                    <Typography variant="body2">
                      {h.amount > 0 ? "+" : ""}{h.amount} — {h.activityType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.createdAt ? new Date(h.createdAt).toLocaleString() : ""}
                    </Typography>
                    {h.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {h.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
